// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import waitFor from "@insite/client-framework/Common/Utilities/waitFor";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getCurrentAccountState } from "@insite/client-framework/Store/Data/Accounts/AccountsSelector";
import loadCurrentAccount from "@insite/client-framework/Store/Data/Accounts/Handlers/LoadCurrentAccount";
import {
    getReviewAndPayCartState,
    isCartLoadedWithoutLines,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import { getCurrentPage, getQueryStrings } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import {
    getDiscountTotal,
    getReviewAndPayPromotions,
} from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import loadDataIfNeeded from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/LoadDataIfNeeded";
import setCartId from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SetCartId";
import setInitialValues from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SetInitialValues";
import setPaymentDetailsState from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SetPaymentDetailsState";
import { AnyAction } from "@insite/client-framework/Store/Reducers";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import TwoButtonModal, { TwoButtonModalStyles } from "@insite/content-library/Components/TwoButtonModal";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Page from "@insite/mobius/Page";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import { CustomAppState } from "@turnerBlueprint/App/Store/Reducers";
import React, { createContext, Dispatch, useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: CustomAppState) => {
    const { cartId } = getQueryStrings(state);
    const cartState = getReviewAndPayCartState(state);
    const { paymentDetailsState } = state.pages.onePageCheckout;
    const promotionsDataView = getReviewAndPayPromotions(state);
    const location = getCurrentPage(state);

    const isLoadDataNeeded =
        ((isCartLoadedWithoutLines(cartState) || !cartState.value?.carriers) && !cartState.isLoading) ||
        (!promotionsDataView.value && !promotionsDataView.isLoading);

    // XNG-Changes
    let discountTotal;
    if (promotionsDataView.value) {
        discountTotal = getDiscountTotal(promotionsDataView.value);
    }

    return {
        cartId,
        cartState,
        isLoadDataNeeded: isLoadDataNeeded || !getCurrentCountries(state),
        isUpdatingCustomer: state.pages.onePageCheckout.isUpdatingCustomer,
        shouldLoadAccount: !getCurrentAccountState(state).value,
        location,
        paymentDetailsState,
        // XNG-Changes
        xGAState: state.xGA,
        isLoading:
            !cartState.value ||
            cartState.isLoading ||
            state.pages.onePageCheckoutConfirmation.isPreloadingData ||
            state.pages.onePageCheckout.isSavingShipping,
        isPlacingOrder: state.pages.onePageCheckout.isPlacingOrder,
        homePageUrl: getPageLinkByPageType(state, "HomePage")?.url,
        confirmationPageUrl:
            getPageLinkByPageType(state, "OnePageCheckoutConfirmationPage")?.url || "/OnePageCheckoutConfirmation",
        cartEmpty: !cartState.isLoading && cartState.value && !cartState.value.cartLines?.length,
        discountTotal,
    };
};

const mapDispatchToProps = {
    loadDataIfNeeded,
    setInitialValues,
    setCartId,
    loadCurrentAccount,
    setPaymentDetailsState,
    // XNG-Changes
    resetXCheckout: () => (dispatch: Dispatch<AnyAction>) => {
        dispatch({ type: "xCheckout/Reset" } as any);
    },
};

// XNG-Changes
export interface OnePageCheckoutPageStyles {
    modal?: TwoButtonModalStyles;
}

// XNG-Changes
export const onePageCheckoutPageStyles: OnePageCheckoutPageStyles = {};

// XNG-Changes
type Props = PageProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps> & HasHistory;

type Validator = () => Promise<boolean>;

type State = {
    formSubmitAttempted: boolean;
    validators: {
        [key: string]: Validator | undefined;
    };
    // XNG-Changes
    formIds?: {
        shippingFormId?: string;
        billingFormId?: string;
        ccBillingFormId?: string;
    };
};

export const OnePageCheckoutFormContext = createContext<State>({
    formSubmitAttempted: false,
    validators: {},
});

const OnePageCheckoutPage = ({
    id,
    cartId,
    isLoadDataNeeded,
    isUpdatingCustomer,
    shouldLoadAccount,
    loadDataIfNeeded,
    setInitialValues,
    setCartId,
    loadCurrentAccount,
    location,
    paymentDetailsState,
    setPaymentDetailsState,
    // XNG-Changes
    cartState,
    resetXCheckout,
    history,
    isPlacingOrder,
    isLoading,
    xGAState,
    homePageUrl,
    confirmationPageUrl,
    cartEmpty,
    discountTotal,
}: Props) => {
    // XNG-Changes
    const [leaveCheckoutModalVisible, setLeaveCheckoutModalVisible] = useState(false);
    const isDone = useRef<string>("");

    const [state] = useState<State>({
        formSubmitAttempted: false,
        validators: {},
    });

    useEffect(() => {
        if (isLoadDataNeeded) {
            loadDataIfNeeded({ cartId, onSuccess: () => setInitialValues() });
        }
    });

    // Xng-Change
    // useEffect(() => {
    //     if (paymentDetailsState === "complete") {
    //         setPaymentDetailsState({ paymentDetailsState: "active" });
    //     }
    // }, [location]);
    
    // <XNG-Changes>
    useEffect(() => {
        if (xGAState.checkoutFlowEvents) {
            (window as any)._GaCheckoutFunnel(
                1,
                cartState.value,
                discountTotal,
                xGAState.includeProductBrand,
                xGAState.includeProductCategory,
            );
        }
    }, []);

    const onChangeState = async (url: string, passModal: boolean) => {
        if (url.indexOf(confirmationPageUrl) > -1 || passModal) {
            return true;
        }

        isDone.current = "";
        setLeaveCheckoutModalVisible(true);
        await waitFor(() => {
            return isDone.current !== "";
        });
        return isDone.current === "Accept";
    };

    useEffect(() => {
        setCartId({ cartId });
        if (!isLoadDataNeeded) {
            setInitialValues();
        }
        if (shouldLoadAccount) {
            loadCurrentAccount();
        }
        // Confirmation Popup
        const _push = history.push;
        const _replace = history.replace;

        history.push = async (url, state: any) => {
            const res = await onChangeState(url, state?.passModal);
            if (res) {
                _push(url, state);
            }
        };
        history.replace = async (url, state: any) => {
            const res = await onChangeState(url, state?.passModal);
            if (res) {
                _replace(url, state);
            }
        };
        return () => {
            history.push = _push;
            history.replace = _replace;

            resetXCheckout();
        };
    }, []);

    useEffect(() => {
        if (cartEmpty) {
            history.push(homePageUrl || "/", { passModal: true });
        }
    }, [cartEmpty]);
    // </XNG-Changes>

    return (
        <Page>
            {/* XNG-Changes: add  isLoading || isPlacingOrder */}
            <LoadingOverlay loading={isUpdatingCustomer || isLoading || isPlacingOrder}>
                <OnePageCheckoutFormContext.Provider value={state}>
                    <Zone zoneName="Content" contentId={id} />
                </OnePageCheckoutFormContext.Provider>
            </LoadingOverlay>
            {/* XNG-Changes */}
            <TwoButtonModal
                headlineText={siteMessage("Custom_CheckoutLeaveWarningModalTitle")}
                messageText={siteMessage("Custom_CheckoutLeaveWarningModalMessage")}
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Leave Checkout")}
                submitTestSelector="leaveCheckout_Button"
                modalIsOpen={leaveCheckoutModalVisible}
                onCancel={() => {
                    isDone.current = "Reject";
                    setLeaveCheckoutModalVisible(false);
                }}
                onSubmit={() => {
                    isDone.current = "Accept";
                    setLeaveCheckoutModalVisible(false);
                }}
                // Xng-Changes
                extendedStyles={onePageCheckoutPageStyles.modal}
            />
        </Page>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(OnePageCheckoutPage)),
    definition: {
        hasEditableUrlSegment: true,
        hasEditableTitle: true,
        pageType: "System",
    },
};

export default pageModule;
